// Monotone cubic resampling, for DISPLAY only.
//
// WHY THIS EXISTS
//
// Pancake's SvgLine emits a polyline — one `L` command per data point, no curve
// commands. That is fine when the data is dense: pdq8 has 101 points across a
// 440px plot, so its chords are 4.4px and the line reads as smooth. hfsgs has
// 19 points across 417px, giving 23.2px straight chords across an S-shaped
// curve, which is visibly faceted. Reported as "the curve appears in bits", and
// measured before being believed.
//
// WHY MONOTONE CUBIC AND NOT A PLAIN SPLINE
//
// These curves are cumulative distributions. A natural cubic spline or
// Catmull-Rom can overshoot between knots, which here would draw a cumulative
// share that dips below a previous value or rises above 1 — visibly wrong, and
// wrong in a way that misrepresents a clinical figure. Fritsch-Carlson
// (PCHIP) tangents cannot overshoot: the interpolant is monotone wherever the
// data is monotone, and it passes exactly through every published point.
//
// WHAT THIS IS NOT
//
// It is not new data. Every published row is still a point on the drawn line,
// and the percentile the patient is told still comes from the original table
// via each tool's own cdf(). This only adds intermediate pixels between points
// that a fitted continuous distribution already implies.

/**
 * Fritsch-Carlson tangents: monotone, no overshoot.
 * @param {number[]} xs strictly increasing
 * @param {number[]} ys
 * @returns {number[]} tangent at each knot
 */
function monotoneTangents(xs, ys) {
	const n = xs.length;
	const h = [], delta = [];
	for (let i = 0; i < n - 1; i++) {
		h[i] = xs[i + 1] - xs[i];
		delta[i] = (ys[i + 1] - ys[i]) / h[i];
	}

	const m = new Array(n);
	m[0] = delta[0];
	m[n - 1] = delta[n - 2];
	for (let i = 1; i < n - 1; i++) {
		// A sign change (or a flat run) is a local extremum. Forcing the tangent
		// to zero there is what stops the curve bulging past the knot.
		if (delta[i - 1] * delta[i] <= 0) {
			m[i] = 0;
		} else {
			const w1 = 2 * h[i] + h[i - 1];
			const w2 = h[i] + 2 * h[i - 1];
			m[i] = (w1 + w2) / (w1 / delta[i - 1] + w2 / delta[i]); // weighted harmonic mean
		}
	}
	return m;
}

/**
 * Resample a monotone series to a denser one for drawing.
 *
 * @param {Array<object>} rows   source rows, already in ascending x order
 * @param {(r: object) => number} getX
 * @param {(r: object) => number} getY
 * @param {number} perInterval   intermediate points to insert between knots
 * @returns {Array<{x: number, y: number}>} dense points, originals included
 */
export function resampleMonotone(rows, getX, getY, perInterval = 8) {
	const xs = rows.map(getX);
	const ys = rows.map(getY);
	if (xs.length < 3) return xs.map((x, i) => ({ x, y: ys[i] }));

	const m = monotoneTangents(xs, ys);
	const out = [];
	for (let i = 0; i < xs.length - 1; i++) {
		const h = xs[i + 1] - xs[i];
		// Emit the knot itself, then the interior samples. The final knot is
		// appended after the loop so it appears exactly once.
		for (let k = 0; k < perInterval; k++) {
			const t = k / perInterval;
			const t2 = t * t;
			const t3 = t2 * t;
			// Hermite basis
			const h00 = 2 * t3 - 3 * t2 + 1;
			const h10 = t3 - 2 * t2 + t;
			const h01 = -2 * t3 + 3 * t2;
			const h11 = t3 - t2;
			out.push({
				x: xs[i] + t * h,
				y: h00 * ys[i] + h10 * h * m[i] + h01 * ys[i + 1] + h11 * h * m[i + 1]
			});
		}
	}
	out.push({ x: xs[xs.length - 1], y: ys[ys.length - 1] });
	return out;
}
